import React from "react";
import { Table, Tag, Button, Dropdown, Empty, Tooltip, Modal } from "antd";
import {
  EditOutlined,
  MoreOutlined,
  HistoryOutlined,
  CheckOutlined,
  StopOutlined,
  WarningOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { RawMaterial, RawMaterialAction } from "../../../Types";
import { RecordStatus } from "../../../Enums";
import { StockBatchesTable } from "./StockBatchesTable";
import { PAGE_SIZE } from "../../../Constant";
import { tablePagination } from "../../../components";
import { useSectionAccess } from "../../../Services";

interface Props {
  materialsList: RawMaterial[];
  total: number;
  page: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onEdit: (rec: RawMaterial) => void;
  onShowHistory: (rec: RawMaterial) => void;
  onToggleStatus: (rec: RawMaterial) => void;
  onDelete: (rec: RawMaterial) => void;
}

export const RawMaterialsTable: React.FC<Props> = ({
  materialsList,
  total,
  page,
  isLoading,
  onPageChange,
  onEdit,
  onShowHistory,
  onToggleStatus,
  onDelete,
}) => {
  const { isAllowed } = useSectionAccess("raw_materials");
  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (v: string) => (
        <span className="font-semibold text-gray-600 text-xs">{v}</span>
      ),
    },
    {
      title: "Material",
      dataIndex: "name",
      key: "name",
      render: (v: string) => (
        <span className="font-medium text-gray-800 text-sm">{v}</span>
      ),
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      render: (v: string) => <Tag className="text-xs capitalize">{v}</Tag>,
    },
    {
      title: "Stock",
      key: "total_stock",
      render: (_: any, rec: RawMaterial) => {
        const isLow = rec.total_quantity <= rec.alert_quantity;
        return (
          <div className="flex items-center gap-1.5">
            <span
              className={`text-sm font-semibold ${isLow ? "text-red-600" : "text-gray-800"}`}
            >
              {rec.total_quantity} {rec.unit}
            </span>
            {isLow && (
              <Tooltip
                title={`Below alert level of ${rec.alert_quantity} ${rec.unit}`}
              >
                <WarningOutlined className="text-red-500 text-xs" />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Suppliers",
      key: "suppliers",
      render: (_: any, rec: RawMaterial) =>
        rec.suppliers.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {rec.suppliers.slice(0, 2).map((s: any) => (
              <Tag key={s.id} className="text-[11px] m-0">
                {s.name}
              </Tag>
            ))}
            {rec.suppliers.length > 2 && (
              <Tag className="text-[11px] m-0">+{rec.suppliers.length - 2}</Tag>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: RecordStatus) => (
        <Tag
          color={s === RecordStatus.ACTIVE ? "success" : "default"}
          className="text-[11px] font-medium"
        >
          {s}
        </Tag>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 48,
      render: (_: any, rec: RawMaterial) => {
        const isActive = rec.status === RecordStatus.ACTIVE;
        const menuItems = [
          ...(isAllowed(RawMaterialAction.Manage)
            ? [
                {
                  key: "edit",
                  label: "Edit Material",
                  icon: <EditOutlined />,
                  onClick: () => onEdit(rec),
                },
              ]
            : []),
          {
            key: "history",
            label: "Stock History",
            icon: <HistoryOutlined />,
            onClick: () => onShowHistory(rec),
          },
          {
            key: "toggle",
            label: isActive ? "Deactivate" : "Activate",
            icon: isActive ? <StopOutlined /> : <CheckOutlined />,
            onClick: () => onToggleStatus(rec),
          },
          ...(isAllowed(RawMaterialAction.Manage)
            ? [
                {
                  key: "delete",
                  label: "Delete Material",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: "Delete Raw Material?",
                      content: `Permanently delete "${rec.name}"? This cannot be undone.`,
                      okText: "Delete",
                      okButtonProps: { danger: true },
                      cancelText: "Cancel",
                      onOk: () => onDelete(rec),
                    });
                  },
                },
              ]
            : []),
        ];
        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              className="text-gray-400 hover:text-gray-700"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <Table
        dataSource={materialsList}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={tablePagination({
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onChange: onPageChange,
          itemName: "raw materials",
        })}
        scroll={{ x: 900 }}
        size="middle"
        expandable={{
          expandedRowRender: (record) => <StockBatchesTable record={record} />,
          rowExpandable: (record) => record.stocks && record.stocks.length > 0,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No raw materials registered yet."
            />
          ),
        }}
      />
    </>
  );
};
